import React, { useEffect, useState } from "react";
import { api, exportUrl } from "@/lib/api";
import { Download, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { STATUS_META } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export function ExportDialog({ triggerClassName = "", triggerLabel = "Export Excel", testId = "export-excel-button" }) {
  const [open, setOpen] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [divisi, setDivisi] = useState("all");
  const [status, setStatus] = useState("all");
  const [branch, setBranch] = useState("all");

  useEffect(() => {
    if (open) {
      api.get("/divisions").then((r) => setDivisions(r.data)).catch(() => {});
      api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    }
  }, [open]);

  const download = () => {
    const url = exportUrl({
      start_date: start || undefined,
      end_date: end || undefined,
      divisi: divisi !== "all" ? divisi : undefined,
      status: status !== "all" ? status : undefined,
      branch: branch !== "all" ? branch : undefined,
    });
    window.open(url, "_blank");
    setOpen(false);
  };

  const reset = () => { setStart(""); setEnd(""); setDivisi("all"); setStatus("all"); setBranch("all"); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className={`border-brand text-brand hover:bg-secondary ${triggerClassName}`} data-testid={testId}>
          <Download className="h-4 w-4 mr-2" /> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-brand" /> Export Laporan ke Excel
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Pilih filter untuk mempersempit data laporan. Kosongkan untuk mengambil semua data.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="start">Tanggal Mulai</Label>
              <Input id="start" type="date" value={start} onChange={(e) => setStart(e.target.value)}
                className="mt-1.5" data-testid="export-start-date" />
            </div>
            <div>
              <Label htmlFor="end">Tanggal Akhir</Label>
              <Input id="end" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                className="mt-1.5" data-testid="export-end-date" />
            </div>
          </div>
          <div>
            <Label>Divisi</Label>
            <Select value={divisi} onValueChange={setDivisi}>
              <SelectTrigger className="mt-1.5" data-testid="export-divisi-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="export-divisi-all">Semua Divisi</SelectItem>
                {divisions.map((d) => <SelectItem key={d} value={d} data-testid={`export-divisi-${d}`}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Cabang</Label>
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="mt-1.5" data-testid="export-branch-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="export-branch-all">Semua Cabang</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`export-branch-${b.id}`}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1.5" data-testid="export-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k} data-testid={`export-status-${k}`}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={reset} data-testid="export-reset-btn">Reset</Button>
          <Button onClick={download} className="bg-brand hover:bg-emerald-800" data-testid="export-download-btn">
            <Download className="h-4 w-4 mr-2" /> Download Excel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
